import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { normalizePaginatedListResponse, PaginatedApiResponse, ListQueryOptions } from '../../services/paginatedList';

export type DocumentStatus = 'DRAFT' | 'IN_APPROVAL' | 'APPROVED' | 'REJECTED';
export type DocumentPostingRole = 'EXPENSE' | 'VAT' | 'PAYABLE' | 'RECEIVABLE' | 'OTHER';
export type DocumentPostingSide = 'DEBIT' | 'CREDIT';
export type DocumentPostingAmountMode = 'SCAN_LINES' | 'TOTAL' | 'BALANCE' | 'VAT_AMOUNT';

export interface DocumentLine {
  id?: number;
  role?: DocumentPostingRole | null;
  companyId?: number | null;
  accountId?: number | null;
  debitLocal?: number | string;
  creditLocal?: number | string;
  narrative?: string | null;
  lineOrder?: number;
  currencyId?: number | null;
  rate?: number | string | null;
  debitUSD?: number | string | null;
  creditUSD?: number | string | null;
  confidence?: number | null;
}

export interface DocumentFileType {
  id: number;
  code: string;
  name: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface DocumentPostingRuleLine {
  id?: number;
  sortOrder?: number;
  role: DocumentPostingRole;
  side: DocumentPostingSide;
  accountId: number;
  amountMode: DocumentPostingAmountMode;
  /** Comma-separated keywords; empty = default catch-all. */
  matchKeywords?: string | null;
  account?: { id: number; code: string; name: string; combLevel?: string | null } | null;
}

export interface DocumentPostingRule {
  id: number;
  code: string;
  name: string;
  fileTypeId: number;
  supplierKey?: string | null;
  documentTypeId?: number | null;
  defaultCompanyId?: number | null;
  isActive?: boolean;
  fileType?: DocumentFileType | null;
  documentType?: { id: number; code: string; name: string } | null;
  defaultCompany?: { id: number; code: string; name: string } | null;
  lines: DocumentPostingRuleLine[];
}

export interface Document {
  id: number;
  status: DocumentStatus;
  title: string;
  summary?: string | null;
  departmentId?: number | null;
  fileTypeId?: number | null;
  supplierName?: string | null;
  invoiceNumber?: string | null;
  documentDate?: string | null;
  totalAmount?: number | string | null;
  payload?: Record<string, unknown> | null;
  aiRaw?: Record<string, unknown> | null;
  aiConfidence?: number | null;
  originalFilename?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  storageKey?: string | null;
  accDocumentId?: number | null;
  approvalId?: number | null;
  createdAt: string;
  updatedAt?: string;
  fileType?: DocumentFileType | null;
  department?: { id: number; name?: string; departmentName?: string } | null;
  lines?: DocumentLine[];
  accDocument?: { id: number } | null;
  approval?: { id: number; status?: string } | null;
  createdByUser?: { id: number; userDisplayName?: string | null; userFirstName: string; userLastName: string } | null;
}

export type DocumentsListView = 'inbox' | 'archive';

export const useDocuments = (
  page = 1,
  search = '',
  options: ListQueryOptions & {
    status?: string;
    departmentId?: number;
    fileTypeId?: number;
    documentDate?: string;
    documentDateFrom?: string;
    documentDateTo?: string;
    view?: DocumentsListView;
    limit?: number;
  } = {},
) => {
  const limit = options.limit && options.limit > 0 ? options.limit : 25;
  const view = options.view || 'inbox';
  const params = new URLSearchParams({ page: String(page), limit: String(limit), view });
  if (search.trim()) params.set('search', search.trim());
  if (options.status) params.set('status', options.status);
  if (options.departmentId) params.set('departmentId', String(options.departmentId));
  if (options.fileTypeId) params.set('fileTypeId', String(options.fileTypeId));
  if (options.documentDate?.trim()) params.set('documentDate', options.documentDate.trim());
  if (options.documentDateFrom?.trim()) params.set('documentDateFrom', options.documentDateFrom.trim());
  if (options.documentDateTo?.trim()) params.set('documentDateTo', options.documentDateTo.trim());
  if (options.sortKey) {
    params.set('sortKey', options.sortKey);
    params.set('sortDirection', options.sortDirection === 'asc' ? 'asc' : 'desc');
  }
  const q = `?${params.toString()}`;
  const result = useSWR<PaginatedApiResponse<Document> | Document[]>(`/documents${q}`, fetcher);
  const normalized = normalizePaginatedListResponse(result.data, page, limit);
  const totalPages =
    result.data && !Array.isArray(result.data) && typeof result.data.totalPages === 'number'
      ? result.data.totalPages
      : Math.max(1, Math.ceil(normalized.total / limit) || 1);
  return {
    data: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    totalPages,
    isLoading: result.isLoading,
    isError: result.error,
    mutate: result.mutate,
  };
};

export const useDocument = (id?: number) => {
  const result = useSWR<Document>(id ? `/documents/${id}` : null, fetcher);
  return { data: result.data, item: result.data, isLoading: result.isLoading, isError: result.error, mutate: result.mutate };
};

export const useDocumentFileTypes = () => useSWR<DocumentFileType[]>('/document-file-types', fetcher);
export const useDocumentPostingRules = () => useSWR<DocumentPostingRule[]>('/document-posting-rules', fetcher);
